import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO.jsx';
import PublicFonts from '../components/PublicFonts.jsx';
import './landing.css';

// Landing page v3 — "the night-shift log".
// The page is narrated as one 2am callout, resolved: every section eyebrow is a
// timestamped log line (the product's core claim IS the timestamped audit trail).
// Signature element: a 3D pointer-tracked phone in the hero that endlessly plays
// the real dispatch loop (ticket → ring L1 → escalate → ring L2 → accept → job chat).
// All motion is CSS transforms + one rAF loop; prefers-reduced-motion respected.

const LANDING_STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'Giraops',
      description: '24/7 service hotline for vendors who keep critical equipment running. Persistent escalation, customer-facing intake, built-in job chat.',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, Android, iOS',
      offers: [
        { '@type': 'Offer', price: '0', priceCurrency: 'AUD', name: 'Free' },
        { '@type': 'Offer', price: '16', priceCurrency: 'AUD', name: 'Basic' },
        { '@type': 'Offer', price: '29', priceCurrency: 'AUD', name: 'Enterprise' },
      ],
    },
    {
      '@type': 'Organization',
      name: 'Giraops',
      url: 'https://giraops.com',
      logo: 'https://giraops.com/giraops-logo.png',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'support.proftek@gmail.com',
        contactType: 'Customer Support',
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Motion hooks
// ---------------------------------------------------------------------------

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

// Adds .in to every .lp3-rv / .lp3-rail inside the root when it scrolls into view.
function useRevealRoot() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll('.lp3-rv, .lp3-rail');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

// Pointer-tracked 3D tilt for the hero phone (rAF-lerped; writes transform
// directly to the node so it never re-renders React).
function useTilt(enabled) {
  const stageRef = useRef(null);
  const phoneRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    const stage = stageRef.current;
    const phone = phoneRef.current;
    if (!stage || !phone) return;
    let target = { x: 0, y: 0 };
    let current = { x: 0, y: 0 };
    let raf = null;
    const tick = () => {
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;
      phone.style.transform = `rotateY(${current.x}deg) rotateX(${current.y}deg)`;
      if (Math.abs(current.x - target.x) > 0.01 || Math.abs(current.y - target.y) > 0.01) {
        raf = requestAnimationFrame(tick);
      } else {
        raf = null;
      }
    };
    const kick = () => { if (raf == null) raf = requestAnimationFrame(tick); };
    const onMove = (e) => {
      const r = stage.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      target = { x: nx * 16, y: -ny * 11 };
      kick();
    };
    const onLeave = () => { target = { x: 0, y: 0 }; kick(); };
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerleave', onLeave);
    return () => {
      stage.removeEventListener('pointermove', onMove);
      stage.removeEventListener('pointerleave', onLeave);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, [enabled]);
  return { stageRef, phoneRef };
}

// ---------------------------------------------------------------------------
// The dispatch loop — the hero phone's self-playing demo
// ---------------------------------------------------------------------------

const BEATS = [
  { id: 'incoming', dur: 2400 },
  { id: 'ring1', dur: 4600 },
  { id: 'escalate', dur: 2100 },
  { id: 'ring2', dur: 3600 },
  { id: 'accepted', dur: 2600 },
  { id: 'whatsapp', dur: 3400 },
];

const BEAT_LOG = {
  incoming: ['02:14:07', 'ticket #4211 logged — refrigeration'],
  ring1: ['02:14:09', 'ringing L1 · Sam R. [window 5:00]'],
  escalate: ['02:19:09', 'no answer — escalated L1 → L2'],
  ring2: ['02:19:10', 'ringing L2 · Alex T. [window 5:00]'],
  accepted: ['02:21:34', 'accepted by Alex T. (L2)'],
  whatsapp: ['02:21:41', 'job chat open · full record on ticket'],
};

function useDispatchLoop() {
  const [beatIdx, setBeatIdx] = useState(0);
  const [logLines, setLogLines] = useState([BEAT_LOG.incoming]);
  const [secondsLeft, setSecondsLeft] = useState(299);

  // Advance beats on their own clocks; loop forever.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = (beatIdx + 1) % BEATS.length;
      setBeatIdx(next);
      const entry = BEAT_LOG[BEATS[next].id];
      setLogLines((prev) => (next === 0 ? [entry] : [...prev, entry].slice(-5)));
      if (BEATS[next].id === 'ring1') setSecondsLeft(299);
      if (BEATS[next].id === 'ring2') setSecondsLeft(299);
    }, BEATS[beatIdx].dur);
    return () => clearTimeout(t);
  }, [beatIdx]);

  // Tick the countdown while ringing.
  const beatId = BEATS[beatIdx].id;
  useEffect(() => {
    if (beatId !== 'ring1' && beatId !== 'ring2') return;
    const iv = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(iv);
  }, [beatId]);

  return { beatId, logLines, secondsLeft };
}

function CountdownDial({ secondsLeft }) {
  const total = 300;
  const C = 2 * Math.PI * 20;
  const offset = C * (1 - secondsLeft / total);
  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, '0');
  return (
    <div className="lp3-countdown">
      <svg className="lp3-dial" viewBox="0 0 46 46" aria-hidden="true">
        <circle className="bg" cx="23" cy="23" r="20" />
        <circle className="fg" cx="23" cy="23" r="20" strokeDasharray={C} strokeDashoffset={offset} />
      </svg>
      <div>
        <span className="lp3-count-num">{mm}:{ss}</span>
        <span className="lp3-count-lbl">UNTIL ESCALATION</span>
      </div>
    </div>
  );
}

function PhoneDemo({ reduced }) {
  const { beatId, logLines, secondsLeft } = useDispatchLoop();
  const { stageRef, phoneRef } = useTilt(!reduced);

  const on = (id) => `lp3-beat${beatId === id ? ' on' : ''}`;

  return (
    <div className="lp3-stage" ref={stageRef}>
      <div className="lp3-phone" ref={phoneRef}>
        <div className="lp3-screen" aria-hidden="true">
          <div className="lp3-statusbar">
            <span>02:{beatId === 'incoming' || beatId === 'ring1' ? '14' : '21'} AM</span>
            <span className="lp3-live"><span className="lp3-live-dot" />GIRAOPS ON-CALL</span>
          </div>

          <div className="lp3-screen-main">
            <div className={on('incoming')}>
              <div className="lp3-card">
                <div className="lp3-card-tag lp3-tag-esc">Incoming ticket</div>
                <h4>Cool room down — temp rising</h4>
                <p>Westfield Bondi · night manager · refrigeration</p>
              </div>
            </div>

            <div className={on('ring1')}>
              <div className="lp3-card lp3-card-ring">
                <div className="lp3-card-tag lp3-tag-ring">Ringing L1 · Sam Reyes</div>
                <h4>Cool room down — temp rising</h4>
                <p>Westfield Bondi · stock at risk</p>
                <CountdownDial secondsLeft={secondsLeft} />
                <div className="lp3-btnrow">
                  <span className="lp3-mini lp3-mini-accept">Accept</span>
                  <span className="lp3-mini lp3-mini-pass">Pass</span>
                </div>
              </div>
            </div>

            <div className={on('escalate')}>
              <div className="lp3-card">
                <div className="lp3-card-tag lp3-tag-esc">No answer at L1</div>
                <h4>Escalating to Level 2</h4>
                <p>Sam didn't pick up — the chain moves on. Nothing waits.</p>
              </div>
            </div>

            <div className={on('ring2')}>
              <div className="lp3-card lp3-card-ring">
                <div className="lp3-card-tag lp3-tag-ring">Ringing L2 · Alex Tran</div>
                <h4>Cool room down — temp rising</h4>
                <p>Westfield Bondi · stock at risk</p>
                <CountdownDial secondsLeft={secondsLeft} />
                <div className="lp3-btnrow">
                  <span className="lp3-mini lp3-mini-accept">Accept</span>
                  <span className="lp3-mini lp3-mini-pass">Pass</span>
                </div>
              </div>
            </div>

            <div className={on('accepted')}>
              <div className="lp3-card" style={{ borderColor: 'rgba(74,222,128,0.5)' }}>
                <div className="lp3-card-tag lp3-tag-ok">Accepted · Level 2</div>
                <h4>Alex Tran is on it</h4>
                <p>Everyone can see it's handled. Timer cancelled.</p>
              </div>
            </div>

            <div className={on('whatsapp')}>
              <div className="lp3-card" style={{ borderColor: 'rgba(74,222,128,0.5)' }}>
                <div className="lp3-card-tag lp3-tag-ok">Job chat</div>
                <h4>Conversation on the record</h4>
                <div className="lp3-wa">💬 "On my way — send a photo of the panel?"</div>
              </div>
            </div>
          </div>

          <div className="lp3-screenlog">
            {logLines.map(([t, line], i) => (
              <div key={`${t}-${i}`}><span className="t">{t}</span>  {line}</div>
            ))}
          </div>
        </div>
      </div>
      <div className="lp3-phone-shadow" />
      <p className="lp3-sr">
        Demo of a Giraops dispatch: a refrigeration breakdown is reported at 2:14am, the level-one
        technician is rung with a five-minute window, doesn't answer, the ticket escalates to level
        two, Alex accepts, and the conversation continues in the built-in job chat — every step timestamped.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LandingPage() {
  const reduced = usePrefersReducedMotion();
  const revealRef = useRevealRoot();

  useEffect(() => {
    if (!window.location.hash) return;
    requestAnimationFrame(() => {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <div className="lp3" ref={revealRef}>
      <SEO
        title=""
        description="24/7 service hotline SaaS for vendors who keep critical equipment running. Persistent escalation, customer-facing intake, built-in job chat, QR-code asset reporting. Pilot free, then from $16/seat/month."
        path="/"
        structuredData={LANDING_STRUCTURED_DATA}
      />
      <PublicFonts />

      <nav className="lp3-nav">
        <div className="lp3-nav-in">
          {/* Text brand, not the PNG — the logo file has a white background box
              that reads as a sticker on this dark design. */}
          <a href="#top" className="lp3-brand" aria-label="Giraops home">
            <span>GIRA<em>OPS</em></span>
          </a>
          <div className="lp3-nav-links">
            <a href="#how">How it works</a>
            <a href="#who">Who it's for</a>
            <a href="#internal">Internal teams</a>
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/signup" className="lp3-nav-cta">Get early access</Link>
          </div>
        </div>
      </nav>

      <header className="lp3-hero" id="top">
        <div className="lp3-floor" aria-hidden="true" />
        <div className="lp3-orb lp3-orb-a" aria-hidden="true" />
        <div className="lp3-orb lp3-orb-b" aria-hidden="true" />
        <div className="lp3-hero-in">
          <div>
            <p className="lp3-log">02:14 AM — INCOMING · COOL ROOM DOWN, BONDI JUNCTION</p>
            <h1 className="lp3-h1">The call that <em>won't</em> go unanswered.</h1>
            <p className="lp3-hero-sub">
              Giraops rings your on-call tech until someone <strong>accepts</strong> — and
              escalates when they don't. Automatic dispatch, voice + SMS fallback, built-in
              job chat. <strong>Every step on the record.</strong>
            </p>
            <div className="lp3-cta-row">
              <Link to="/signup" className="lp3-btn lp3-btn-primary">Get early access — free</Link>
              <a href="#how" className="lp3-btn lp3-btn-ghost">Watch the chain work</a>
            </div>
            <p className="lp3-fineprint">PILOT PROGRAM · AVAILABLE WORLDWIDE · NO CREDIT CARD</p>
          </div>
          <PhoneDemo reduced={reduced} />
        </div>
      </header>

      <section className="lp3-sec lp3-sec-alt">
        <div className="lp3-wrap">
          <p className="lp3-log lp3-rv">02:14:03 · WHERE IT BREAKS TODAY</p>
          <h2 className="lp3-h2 lp3-rv">Your contracts hang on one number <em>being answered.</em></h2>
          <p className="lp3-sec-sub lp3-rv">
            Most service teams run after-hours on a duty phone, a group chat, and good intentions.
            It works — until the one night it doesn't.
          </p>
          <div className="lp3-problems">
            <div className="lp3-problem lp3-rv">
              <div className="lp3-ptime">02:14 AM</div>
              <h3>The phone is on silent</h3>
              <p>Your on-call tech is asleep. The backup is on holiday. The caller gets voicemail — and starts googling your competitors.</p>
            </div>
            <div className="lp3-problem lp3-rv lp3-rv-d1">
              <div className="lp3-ptime">02:38 AM</div>
              <h3>The group chat scrolled past</h3>
              <p>A message isn't a ring. WhatsApp can't escalate, can't enforce a response window, and keeps no record of who saw what.</p>
            </div>
            <div className="lp3-problem lp3-rv lp3-rv-d2">
              <div className="lp3-ptime">08:05 AM</div>
              <h3>"Where were you?"</h3>
              <p>The customer calls the office. You have no log to point to — just memory, excuses, and a contract renewal that got harder.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="lp3-sec" id="how">
        <div className="lp3-wrap">
          <p className="lp3-log lp3-rv">02:14:09 · THE CHAIN</p>
          <h2 className="lp3-h2 lp3-rv">It doesn't stop ringing. It <em>escalates.</em></h2>
          <p className="lp3-sec-sub lp3-rv">
            A ticket comes in — from your customer's phone, or logged by your office in 15 seconds.
            Then the chain takes over. No human dispatcher required.
          </p>
          <div className="lp3-rail">
            <div className="lp3-rail-line" aria-hidden="true" />
            <div className="lp3-rung">
              <div className="lp3-rung-head">
                <h3>Level 1 — first on-call</h3>
                <span className="lp3-window">WINDOW 5:00</span>
              </div>
              <p>
                A persistent alert hits your L1 tech — push, then SMS, then a voice call if the
                phone stays quiet. It rings until they accept or the window runs out.
              </p>
            </div>
            <div className="lp3-rung">
              <div className="lp3-rung-head">
                <h3>Level 2 — the backup</h3>
                <span className="lp3-window">WINDOW 5:00</span>
              </div>
              <p>
                No answer? The ticket climbs automatically. Availability-aware: it skips whoever's
                off-shift and pulls from the wider pool when the roster has a gap.
              </p>
            </div>
            <div className="lp3-rung">
              <div className="lp3-rung-head">
                <h3>Manager — the safety net</h3>
                <span className="lp3-window">ALWAYS NOTIFIED</span>
              </div>
              <p>
                Escalations, rejections, and abandoned tickets land on the manager's phone as they
                happen — during office hours, reception can be looped in too.
              </p>
            </div>
            <div className="lp3-rung">
              <div className="lp3-rung-head">
                <h3>Resolved — and provable</h3>
              </div>
              <p>
                Accepted in one tap, and the conversation continues in the built-in job chat —
                messages, photos, and every ring, skip, and accept in one timestamped record.
                Your SLA receipt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="lp3-sec lp3-sec-alt">
        <div className="lp3-wrap">
          <p className="lp3-log lp3-rv">02:16:22 · WHAT SHIPS IN THE BOX</p>
          <h2 className="lp3-h2 lp3-rv">Everything the 2am shift needs. <em>Nothing it doesn't.</em></h2>
          <div className="lp3-feats" style={{ marginTop: 40 }}>
            <div className="lp3-feat lp3-rv"><span className="lp3-fk">Dispatch</span>
              <h3>Persistent ringing</h3>
              <p>Configurable response windows per team. No voicemail — it keeps trying until a human accepts.</p>
            </div>
            <div className="lp3-feat lp3-rv lp3-rv-d1"><span className="lp3-fk">Escalate</span>
              <h3>Multi-level chains</h3>
              <p>L1 silent? L2, then the manager. Rejections logged and reported up. Rosters, calendars, sick-day cover built in.</p>
            </div>
            <div className="lp3-feat lp3-rv lp3-rv-d2"><span className="lp3-fk">Route</span>
              <h3>Skill-based routing</h3>
              <p>Refrigeration, controls, electrical — tickets route to the team that owns the skill, automatically.</p>
            </div>
            <div className="lp3-feat lp3-rv"><span className="lp3-fk">Prove</span>
              <h3>Timestamped audit trail</h3>
              <p>Every notification, accept, and escalation logged. When a customer asks "where were you?" — you have the receipt.</p>
            </div>
            <div className="lp3-feat lp3-rv lp3-rv-d1"><span className="lp3-fk">Converse</span>
              <h3>Built-in job chat</h3>
              <p>Tech and requester talk inside the ticket — messages and photos, pushed to their phones, permanently on the job record.</p>
            </div>
            <div className="lp3-feat lp3-rv lp3-rv-d2"><span className="lp3-fk">Install</span>
              <h3>Any phone, in seconds</h3>
              <p>Installs as a web app — no app store, no IT approval. Optional AI assistant answers fault-code questions from your own manuals.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="lp3-sec" id="who">
        <div className="lp3-wrap">
          <p className="lp3-log lp3-rv">02:41 AM · BACK TO BED</p>
          <h2 className="lp3-h2 lp3-rv">Built for the trades that can't afford <em>a missed call.</em></h2>
          <p className="lp3-sec-sub lp3-rv">
            If your customers run equipment that can't tolerate downtime, you're our buyer.
            Available worldwide — we reach wherever a phone rings.
          </p>
          <div className="lp3-pills lp3-rv">
            <span className="lp3-pill">Commercial refrigeration</span>
            <span className="lp3-pill">Industrial HVAC</span>
            <span className="lp3-pill">Generators &amp; backup power</span>
            <span className="lp3-pill">Conveyor &amp; warehouse automation</span>
            <span className="lp3-pill">Material handling &amp; forklifts</span>
            <span className="lp3-pill">Industrial automation &amp; PLC</span>
            <span className="lp3-pill">Medical equipment service</span>
            <span className="lp3-pill">Elevator &amp; lift service</span>
          </div>
        </div>
      </section>

      <section className="lp3-sec lp3-internal" id="internal">
        <div className="lp3-wrap">
          <p className="lp3-log lp3-rv">ALSO RUNNING TONIGHT · YOUR OWN CREW</p>
          <h2 className="lp3-h2 lp3-rv">Have your own maintenance team? <em>Dispatch them like a hotline.</em></h2>
          <p className="lp3-sec-sub lp3-rv">
            Giraops works just as well for the team you already employ — warehouse PLC techs,
            captive electrical crews, hospital biomed, factory maintenance. No customer signup;
            just staff reporting breakdowns and the chain doing its job.
          </p>
          <div className="lp3-int-grid">
            <div className="lp3-int-card lp3-rv">
              <strong>Manual dispatch by phone</strong>
              <p>An admin types a number + description. Ticket created, on-call paged. No public intake required.</p>
            </div>
            <div className="lp3-int-card lp3-rv lp3-rv-d1">
              <strong>AI assistant for your techs</strong>
              <p>Upload your equipment manuals; your on-call electrician asks "what does fault E04 mean" and gets the page citation.</p>
            </div>
            <div className="lp3-int-card lp3-rv lp3-rv-d2">
              <strong>The same 24/7 chain</strong>
              <p>Roster, calendar, sick-day cover, voice + SMS fallback — the on-call plumbing you'd otherwise pay enterprise prices for.</p>
            </div>
            <div className="lp3-int-card lp3-rv lp3-rv-d3">
              <strong>Private, captive use</strong>
              <p>Turn off customer-facing intake entirely. Giraops becomes your internal dispatch + on-call ops tool.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="lp3-final">
        <div className="lp3-final-glow" aria-hidden="true" />
        <div className="lp3-wrap">
          <p className="lp3-log lp3-rv">02:41:12 · TICKET #4211 CLOSED — EVERY STEP LOGGED</p>
          <h2 className="lp3-h2 lp3-rv">Join the <em>pilot.</em></h2>
          <p className="lp3-final-sub lp3-rv">
            Free during early access. Set up your team in 15 minutes, send a test ticket from
            your own phone, and decide if it's worth keeping.
          </p>
          <div className="lp3-cta-row lp3-rv">
            <Link to="/signup" className="lp3-btn lp3-btn-primary">Get early access — free</Link>
            <a
              href="mailto:support.proftek@gmail.com?subject=Giraops%20pilot%20conversation"
              className="lp3-btn lp3-btn-ghost"
            >
              Or book a 20-min call
            </a>
          </div>
        </div>
      </section>

      <footer className="lp3-footer">
        <div className="lp3-footer-in">
          <div><strong style={{ color: 'var(--paper)' }}>Giraops</strong> — 24/7 service hotline · Built in Australia · © 2026</div>
          <div className="lp3-footer-links">
            {/* Plain <a> — static prerendered pages served outside the React router. */}
            <a href="/compare/giraops-vs-signl4">vs SIGNL4</a>
            <a href="/compare/pagerduty-alternative-field-service">PagerDuty alternative</a>
            <a href="/compare/on-call-software-hvac-contractors">For HVAC</a>
            <a href="mailto:support.proftek@gmail.com">Contact</a>
            <Link to="/login">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
